<template>
  <div>
    <Loading v-if="loading" />

    <template v-else-if="activeChallenge && isTiered">
      <TieredChallengeHeader :challenge="challenge" :store-count="challengeStoreCount" />

      <div v-if="showTabs" class="mt-6 mb-4 flex items-center gap-4">
        <ToggleGroup v-model="currentTab" :options="tabOptions" />
        <template v-if="currentTab === 'pilotage' && isOrganizationAdmin && Object.keys(availableFilters).length">
          <SearchBarFilter
            v-for="(filterOptions, filterName, filterIdx) in availableFilters"
            :key="filterName"
            :filterName="filterName"
            :filterIdx="filterIdx"
            :available-filters="availableFilters"
            :selected-filters="selectedFilters"
            @updateFilterSearch="updateFilterSearch"
          />
        </template>
      </div>
      <div v-else class="mt-6"></div>

      <template v-if="currentTab === 'store'">
        <ChallengePerformances
          :challenge="challenge"
          :performance="performance"
          :store-options="storeOptions"
          :selected-store-id="selectedStore?.id"
          :has-multiple-stores="hasMultipleStores"
          @store-change="handleStoreChange"
        />
      </template>

      <template v-else-if="currentTab === 'pilotage'">
        <TieredChallengePilotage
          :challenge="challenge"
          :store-rankings="performance.storeRankings || {}"
          :user-store-ids="pilotageUserStoreIds"
          :weekly-sales="performance.weeklySales || []"
        />
      </template>
    </template>

    <template v-else-if="activeChallenge">
      <div class="px-2 my-4">
        <h1 class="text-4xl font-extrabold tracking-tight text-gray-900">{{ t("title") }}</h1>
      </div>
      <div class="my-5 px-4 py-2 bg-white shadow-sm rounded-lg">
        <Selector
          class="mb-4"
          id="store-selector"
          name="store"
          :label="t('store_label')"
          :modelValue="selectedStore?.id"
          :options="storeOptions"
          @update:modelValue="handleStoreChange"
        />
      </div>
      <ChallengePerformances :challenge="challenge" :performance="performance" />
    </template>

    <div v-else class="p-6 bg-white">
      <p class="uppercase text-2xl">{{ t("no_active_challenge") }}</p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { useI18n } from "vue-i18n";
import ChallengesService from "@/api/services/challenges";
import { isTieredChallenge } from "@/utils/challengeTiers";
import { Loading, Selector, ToggleGroup, SearchBarFilter, toCamelCase } from "@estaly/ui";
import ChallengePerformances from "@/components/challenge/ChallengePerformances.vue";
import TieredChallengePilotage from "@/components/challenge/TieredChallengePilotage.vue";
import TieredChallengeHeader from "@/components/challenge/tiered/TieredChallengeHeader.vue";

export default {
  components: {
    ChallengePerformances,
    TieredChallengePilotage,
    TieredChallengeHeader,
    Loading,
    Selector,
    SearchBarFilter,
    ToggleGroup,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: true,
      challenge: {},
      performance: {},
      challenges: [],
      activeChallenge: null,
      selectedStore: null,
      currentTab: null,
      selectedFilters: {
        ownershipType: [],
        region: [],
      },
    };
  },

  computed: {
    ...mapState("store", { stores: "index" }),
    ...mapGetters("store", ["hasMultipleStores", "getOwnershipTypes"]),
    ...mapGetters("auth", ["isOrganizationAdmin"]),
    ...mapGetters("organization", ["regions"]),

    storeOptions() {
      return this.stores.map((store) => ({
        value: store.id,
        label: store.salesChannel || store.name,
      }));
    },

    userStoreIds() {
      return this.stores.map((s) => s.id);
    },

    isTiered() {
      return isTieredChallenge(this.challenge);
    },

    showTabs() {
      return this.isTiered && this.hasMultipleStores;
    },

    challengeStoreCount() {
      return this.performance.storeRankings?.totalStores || 0;
    },

    tabOptions() {
      return [
        { value: "pilotage", label: this.t("tab_pilotage") },
        { value: "store", label: this.t("tab_store") },
      ];
    },

    ownershipTypeFilterOptions() {
      return this.getOwnershipTypes().map((value) => ({
        value,
        label: this.$t(`store.ownership_type.${value}`),
      }));
    },

    regionFilterOptions() {
      return this.regions.map((region) => ({ value: region, label: region }));
    },

    availableFilters() {
      const filters = {};
      if (this.ownershipTypeFilterOptions.length) {
        filters.ownershipType = this.ownershipTypeFilterOptions;
      }
      if (this.regionFilterOptions.length) {
        filters.region = this.regionFilterOptions;
      }
      return filters;
    },

    hasActiveFilters() {
      return this.selectedFilters.ownershipType.length > 0 || this.selectedFilters.region.length > 0;
    },

    pilotageUserStoreIds() {
      return this.hasActiveFilters ? [] : this.userStoreIds;
    },
  },

  async mounted() {
    this.fetchOrganization();
    if (this.stores.length > 0) {
      this.selectedStore = this.stores[0];
      await this.retrieveChallenges();
      this.currentTab = this.showTabs ? "pilotage" : "store";
    }
  },

  methods: {
    ...mapActions("organization", { fetchOrganization: "fetch" }),

    async retrieveChallenges() {
      this.loading = true;

      const response = await ChallengesService.fetchIndex(this.selectedStore.id, { perPage: 2000 });
      if (response?.success) {
        const data = toCamelCase(response.data);
        this.challenges = data.challenges;
        await this.retrieveActiveChallengePerformances();
      }
      this.loading = false;
    },

    async retrieveActiveChallengePerformances() {
      this.activeChallenge = this.challenges.find((c) => c.active) || null;
      this.challenge = {};
      this.performance = {};

      if (!this.activeChallenge) return;

      const response = await ChallengesService.fetchPerformance(this.selectedStore.id, this.activeChallenge.id, {
        region: this.selectedFilters.region,
        ownershipType: this.selectedFilters.ownershipType,
      });
      if (response?.success) {
        const data = toCamelCase(response.data);
        this.challenge = data.challenge;
        this.performance = data.performance;
      }
    },

    async updateFilterSearch(filterName, value) {
      this.selectedFilters[filterName] = value;
      if (!this.activeChallenge) return;

      const response = await ChallengesService.fetchPerformance(this.selectedStore.id, this.activeChallenge.id, {
        region: this.selectedFilters.region,
        ownershipType: this.selectedFilters.ownershipType,
      });
      if (response?.success) {
        const data = toCamelCase(response.data);
        this.performance = data.performance;
      }
    },

    async handleStoreChange(storeId) {
      const newStore = this.stores.find((store) => store.id === parseInt(storeId, 10));
      this.selectedStore = newStore;
      await this.retrieveChallenges();
    },
  },
};
</script>

<i18n src="./CurrentChallenge.i18n.json"></i18n>
